import React from 'react';
import { FiDownload, FiAlertCircle } from 'react-icons/fi';

import type { PreClientDetail } from '@/features/onboarding/types';

interface ClientDetailViewProps {
  client: PreClientDetail;
  alertMessage?: string;
  onOpenDocument: (title: string, documentId?: string) => void;
}

/**
 * Format CPF or CNPJ document number
 */
const formatDocument = (doc: string): string => {
  if (!doc) return '-';
  const cleanDoc = doc.replace(/\D/g, '');
  if (cleanDoc.length === 11) {
    return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (cleanDoc.length === 14) {
    return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return doc;
};

/**
 * Format phone number
 */
const formatPhone = (phone: string | null): string => {
  if (!phone) return '-';
  const cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length === 11) {
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (cleanPhone.length === 10) {
    return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
};

/**
 * Format CEP
 */
const formatCep = (cep: string | null): string => {
  if (!cep) return '-';
  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length === 8) {
    return cleanCep.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return cep;
};

/**
 * Format currency
 */
const formatCurrency = (value: number | null): string => {
  if (value === null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/**
 * Format date and time
 */
const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Get person type badge
 */
const getPersonTypeBadge = (type: 'F' | 'J', description: string): string => {
  return description ? description : type === 'F' ? 'Pessoa Física' : 'Pessoa Jurídica';
};

export const ClientDetailView: React.FC<ClientDetailViewProps> = ({
  client,
  alertMessage,
  onOpenDocument,
}) => {
  const isPJ = client.type === 'J';

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold text-neutral-800">{client.aliasName ?? client.name}</h1>
          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
            {getPersonTypeBadge(client.type, client.description)}
          </span>
          {client.isBlacklisted && (
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              Blocklist
            </span>
          )}
        </div>
      </div>

      {/* Alert */}
      {alertMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{alertMessage}</p>
        </div>
      )}

      {/* Rejection reason alert */}
      {client.reasonRejection && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800 font-medium">Motivo da reprovação</p>
            <p className="text-sm text-red-700">{client.reasonRejection}</p>
            {client.categoryRejectedName && (
              <p className="text-xs text-red-600 mt-1">Categoria: {client.categoryRejectedName}</p>
            )}
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Informações básicas */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-purple-600 rounded-full"></span>
            Informações básicas
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-neutral-500 mb-1">
                {isPJ ? 'Razão social' : 'Nome completo'}
              </p>
              <p className="text-sm text-neutral-800 font-medium">{client.name}</p>
            </div>
            {client.aliasName && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">
                  {isPJ ? 'Nome fantasia' : 'Nome social'}
                </p>
                <p className="text-sm text-neutral-800 font-medium">{client.aliasName}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-neutral-500 mb-1">{isPJ ? 'CNPJ' : 'CPF'}</p>
              <p className="text-sm text-neutral-800 font-medium">
                {formatDocument(client.numberDocument)}
              </p>
            </div>
            {client.invoicing !== null && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">Faturamento (Últimos 3 meses)</p>
                <p className="text-sm text-neutral-800 font-medium">
                  {formatCurrency(client.invoicing)}
                </p>
              </div>
            )}
            {client.birthFoundationDate && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">
                  {isPJ ? 'Data de fundação' : 'Data de nascimento'}
                </p>
                <p className="text-sm text-neutral-800 font-medium">
                  {new Date(client.birthFoundationDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
            )}
            {isPJ && client.legalNatureDescription && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">Natureza jurídica</p>
                <p className="text-sm text-neutral-800 font-medium">
                  {client.legalNatureNumber} - {client.legalNatureDescription}
                </p>
              </div>
            )}
            {!isPJ && client.cbo && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">CBO</p>
                <p className="text-sm text-neutral-800 font-medium">{client.cbo}</p>
              </div>
            )}
          </div>
        </div>

        {/* Contato */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-purple-600 rounded-full"></span>
            Contato
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-neutral-500 mb-1">Telefone</p>
              <p className="text-sm text-neutral-800 font-medium">
                {formatPhone(client.telephone)}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500 mb-1">Celular</p>
              <p className="text-sm text-neutral-800 font-medium">
                {formatPhone(client.cellphone)}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500 mb-1">Email</p>
              <p className="text-sm text-neutral-800 font-medium">{client.email || '-'}</p>
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-purple-600 rounded-full"></span>
            Endereço
          </h2>
          {client.address ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">CEP</p>
                  <p className="text-sm text-neutral-800 font-medium">
                    {formatCep(client.address.cep)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Cidade</p>
                  <p className="text-sm text-neutral-800 font-medium">{client.address.city}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Rua/Avenida</p>
                  <p className="text-sm text-neutral-800 font-medium">{client.address.street}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">UF</p>
                  <p className="text-sm text-neutral-800 font-medium">{client.address.uf}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Número</p>
                  <p className="text-sm text-neutral-800 font-medium">
                    {client.address.addressNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Complemento</p>
                  <p className="text-sm text-neutral-800 font-medium">
                    {client.address.complement ?? '-'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Bairro</p>
                <p className="text-sm text-neutral-800 font-medium">
                  {client.address.neighborhood}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-neutral-500">Endereço não informado</p>
          )}
        </div>
      </div>

      {/* Documentos */}
      {client.listDocuments.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4">
            Documentos {isPJ ? 'empresa' : 'pessoais'}
          </h2>
          <div className="flex gap-4 flex-wrap">
            {client.listDocuments.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => {
                  onOpenDocument(doc.type.description, doc.id);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <FiDownload className="w-4 h-4" />
                {doc.type.description}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resultado das consultas */}
      {(client.dictAnalysis ?? client.quodRufraAnalysis ?? client.dimensaAnalysis) && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4">
            Resultado das consultas do cliente
          </h2>
          <div className="flex gap-8 justify-around">
            {client.dictAnalysis && (
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">Dict</p>
                <p className="text-xs text-neutral-500">
                  {formatDateTime(client.dictAnalysis.createdAt)}
                </p>
              </div>
            )}
            {client.quodRufraAnalysis && (
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">Rufra</p>
                <p className="text-xs text-neutral-500">
                  {formatDateTime(client.quodRufraAnalysis.createdAt)}
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  Score: {client.quodRufraAnalysis.score}
                </p>
              </div>
            )}
            {client.dimensaAnalysis && (
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">Dimensa</p>
                <p className="text-xs text-neutral-500">
                  {formatDateTime(client.dimensaAnalysis.createdAt)}
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  Score: {client.dimensaAnalysis.score}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sócios */}
      {client.listPreAssociates.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-4">Sócios</h2>
          <div className="space-y-4">
            {client.listPreAssociates.map((associate) => (
              <div
                key={associate.id}
                className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-neutral-800">{associate.name}</p>
                  <p className="text-xs text-neutral-500">
                    {formatDocument(associate.numberDocument)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-neutral-700">{associate.participationPercentage}%</p>
                  {associate.isAdministrator && (
                    <span className="text-xs text-purple-600">Administrador</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
